import {
  AddressType,
  BillingAddress,
  UniqueAddressMap,
  UniqueAddressValue,
} from '@sola/schema/dotcom/shared/address.js';
import {
  GraphQLPaymentMethodBillingAddress,
  GraphQLSupporterAddress,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';

// Helper function to create a unique hash from an address object (using street, city, postalCode, etc.)
export const createAddressHash = (
  street: string,
  city: string,
  postalCode: string,
): string | undefined => {
  if (street && city && postalCode) {
    return `${street}-${city}-${postalCode}`;
  }
};

const createAddressText = ({
  billingAddress,
  billingAddress2,
  billingCity,
  billingState,
  billingPostal,
}: BillingAddress) =>
  `${billingAddress}${billingAddress2 ? ` ${billingAddress2}` : ''}, ${billingCity}, ${billingState}, ${billingPostal}`;

const mapSupporterAddress = (
  address: GraphQLSupporterAddress,
): UniqueAddressValue => {
  const { street, street2, city, country, postalCode, state } = address;
  const billingAddress: BillingAddress = {
    billingAddress: street ?? '',
    billingAddress2: street2 ?? '',
    billingCity: city ?? '',
    billingCountry: country ?? '',
    billingPostal: postalCode ?? '',
    billingState: state ?? '',
  };
  return {
    type: AddressType.Mailing,
    text: createAddressText(billingAddress),
    billingAddress,
  };
};

const mapPaymentMethodAddress = (
  address: GraphQLPaymentMethodBillingAddress,
): UniqueAddressValue => {
  const { street, city, country, postalCode, stateCode } = address;
  const billingAddress: BillingAddress = {
    billingAddress: street ?? '',
    billingAddress2: '',
    billingCity: city ?? '',
    billingCountry: country ?? '',
    billingPostal: postalCode ?? '',
    billingState: stateCode ?? '',
  };

  return {
    type: AddressType.Billing,
    text: createAddressText(billingAddress),
    billingAddress,
  };
};

/**
 * Gets a map of unique billing addresses attached to a supporter.
 * @param supporterAddress The supporter's contact address.
 * @param billingAddresses The supporter's billing addresses.
 * @returns The map of billing addresses.
 */
const getAllUniqueSupporterAddresses = (
  supporterAddress: GraphQLSupporterAddress,
  billingAddresses: GraphQLPaymentMethodBillingAddress[] = [],
): UniqueAddressMap => {
  const mappedSupporterAddress = mapSupporterAddress(supporterAddress);
  const mappedPaymentMethodAddresses = billingAddresses.map((address) =>
    mapPaymentMethodAddress(address),
  );
  const allAddresses = [
    mappedSupporterAddress,
    ...mappedPaymentMethodAddresses,
  ];

  return allAddresses.reduce((all: UniqueAddressMap, address) => {
    if (!address) {
      return all;
    }

    const { billingAddress, billingCity, billingPostal } =
      address.billingAddress;
    const hash = createAddressHash(billingAddress, billingCity, billingPostal);

    if (!hash) {
      return all;
    }

    all.set(hash, address);
    return all;
  }, new Map());
};

/**
 * Gets an array of unique billing addresses attached to a supporter.
 * @param supporterAddress The supporter's contact address.
 * @param paymentMethods The supporter's payment methods.
 * @returns The array of billing addresses.
 */
export const getSupporterAddresses = (
  supporterAddress: GraphQLSupporterAddress,
  paymentMethods?: GraphQLSupporterPaymentMethod[],
): UniqueAddressMap => {
  // Collect all supporter saved paymentMethods
  const creditCards = paymentMethods?.filter(({ creditCard }) => creditCard);
  const billingAddresses = creditCards?.map(
    ({ billingAddress }) =>
      billingAddress as GraphQLPaymentMethodBillingAddress,
  );
  return getAllUniqueSupporterAddresses(supporterAddress, billingAddresses);
};
